// EVE-Hero — die zentrale Bühne im Markt-Modus. Avatar + Greeting +
// Letzter-Call-Pill + Composer + Quick-Actions.
// Source-of-truth: mockups/03-shell/B-bolt-hero.html (center).

import { useMemo } from 'react';
import { Radar } from 'lucide-react';
import { Composer, type ComposerSubmitInput } from './composer';
import { EveAvatar } from './eve-avatar';
import { QuickActions } from './quick-actions';
import { useAuth } from '@/hooks/use-auth';
import { useLastSeen } from '@/hooks/use-last-seen';
import { buildHeroGreeting } from '@/lib/hero-greeting';
import type { AgentKey } from '@/types/eve-profile';

export interface EveHeroProps {
  /** Optional override für den Greeting-Namen (default = auth profile nickname). */
  userName?: string;
  /** EVE's letzte Call-Zusammenfassung („PVP · NEUTRAL · MEDIUM"). */
  lastCallSummary?: string;
  /** Wann der letzte Call kam — als Text („vor 12 min"). */
  lastCallAgo?: string;
  /** Wird durchgereicht an Composer. */
  onSubmit?: (input: ComposerSubmitInput) => void | Promise<void>;
  /** Wird durchgereicht an QuickActions. */
  onTrigger?: (agentKey: AgentKey, triggerText: string) => void | Promise<void>;
}

export function EveHero({
  userName,
  lastCallSummary,
  lastCallAgo,
  onSubmit,
  onTrigger,
}: EveHeroProps) {
  const { profile } = useAuth();
  const name = userName ?? profile?.nickname ?? 'Doc';
  const lastSeen = useLastSeen();
  // Dynamische Begrüßung wie im Privat-Chat: Tageszeit + letzter Besuch + Datum.
  const greeting = useMemo(() => buildHeroGreeting(lastSeen, name), [lastSeen, name]);

  return (
    <div className="flex flex-col items-center px-4 pt-6 pb-3">
      <div className="mb-3">
        <EveAvatar size={96} pulse liveDot />
      </div>

      <div className="mb-4 max-w-2xl text-center">
        <h1 className="mb-1 text-3xl font-bold tracking-tight">
          {greeting.daypart},{' '}
          <span className="bg-gradient-to-b from-amber-200 via-amber-400 to-white bg-clip-text text-transparent">
            {name}
          </span>
          .
        </h1>
        <p className="mb-2 text-sm text-neutral-300">{greeting.subline}</p>
        {lastCallSummary ? (
          <div className="glass inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[11px] text-neutral-300">
            <Radar className="size-3 text-amber-400" />
            <span>
              Letzter Call: <strong className="text-amber-300">{lastCallSummary}</strong>
              {lastCallAgo ? ` · ${lastCallAgo}` : ''}
            </span>
          </div>
        ) : (
          <div className="glass inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[11px] text-neutral-400">
            <Radar className="size-3 text-amber-400" />
            <span>EVE wartet auf den ersten Call.</span>
          </div>
        )}
      </div>

      <div className="mb-3 w-full max-w-2xl">
        <Composer onSubmit={onSubmit} />
      </div>

      <QuickActions onTrigger={onTrigger} />
    </div>
  );
}
